import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Size, fileTypesFor, suggestionRowsFor, t, type NdaScopeType, type SuggestionRow, type SuggestionSource } from '@bms2/shared';
import { Checkbox, DatePicker, Dropdown, RadioGroup, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, PICKER_CAP, apiFetch, post, upload, useRows, FILES_PATH, FILE_TYPES_PATH } from './api';
import styles from './RecordPage.module.css';
import { useAddNew } from './AddNewHost';

/**
 * Upload NDA (piece 9; decisions 0071, 0072): the signed agreement enters as
 * a Draft that opens nothing, or as Demanded when the customer requires one
 * and no signed copy is on file. 🔨 The modal sets neither effect: saving
 * opens the review questionnaire, whose confirmed answers are the only writer
 * of requiresCover and enablesRelease. Found while building 2026-09-09, built
 * 2026-09-12.
 */
export const NDA_PATH = '/nda';
export const NDA_REVIEW_ROUTE = '/ndas/review';

export interface NdaModalProps {
  open: boolean;
  apiBaseUrl?: string;
  /** The Company page the modal opened from, pre-set as the counterparty. */
  companyId?: string | null;
  companyName?: string | null;
  onCancel: () => void;
  onSaved: () => void;
}

interface FileTypeRow {
  id: string;
  name: string;
  isActive: boolean;
  appliesTo?: readonly string[];
}

function useListSearch(apiBaseUrl: string, recordType: 'CompanyRecord' | 'Deal' | 'PersonRecord') {
  return useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/${recordType}?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: SuggestionSource[] };
      // 🔨 The two lines come from the shared mapper, so a Person reads as a
      // name over a position rather than as the uuid a per-picker fallback
      // used to print (product owner, 2026-09-15).
      return suggestionRowsFor(recordType, body.rows);
    },
    [apiBaseUrl, recordType],
  );
}

export function NdaModal({ open, apiBaseUrl = '', companyId = null, companyName = null, onCancel, onSaved }: NdaModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [scope, setScope] = useState<NdaScopeType>('Company');
  const [counterparty, setCounterparty] = useState<{ id: string; name: string } | null>(companyId ? { id: companyId, name: companyName ?? companyId } : null);
  const [deal, setDeal] = useState<{ id: string; name: string } | null>(null);
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [demanded, setDemanded] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileTypeId, setFileTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const companies = useListSearch(apiBaseUrl, 'CompanyRecord');
  const deals = useListSearch(apiBaseUrl, 'Deal');
  const people = useListSearch(apiBaseUrl, 'PersonRecord');
  const types = useRows<FileTypeRow>(open ? FILE_TYPES_PATH : null, apiBaseUrl, { cache: true });
  // The offer reads the same record kind the upload below tags, so the server's refusal (decision 1190) cannot meet a type this picker offered.
  const fileTypes = types.state.kind === 'ready' ? fileTypesFor(types.state.data.filter((row) => row.isActive), scope === 'Person' ? 'PersonRecord' : 'CompanyRecord') : [];

  const save = async () => {
    setError(null);
    if (scope !== 'Person' && !counterparty) return setError(t('nda.modal.needsCounterparty'));
    if (scope === 'Deal' && !deal) return setError(t('nda.modal.needsDeal'));
    if (scope === 'Person' && !person) return setError(t('nda.modal.needsPerson'));
    if (!effectiveDate) return setError(t('nda.modal.needsEffective'));
    if (!demanded && !file) return setError(t('nda.modal.needsFile'));
    if (file && !fileTypeId) return setError(t('nda.modal.needsFileType'));
    setBusy(true);
    let fileId: string | null = null;
    if (file) {
      // The signed copy is a File tagged to the counterparty, the same shape a tax exempt certificate uses.
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('fileTypeId', fileTypeId);
      if (counterparty) {
        form.append('taggedRecordType', 'CompanyRecord');
        form.append('taggedRecordId', counterparty.id);
      } else if (person) {
        form.append('taggedRecordType', 'PersonRecord');
        form.append('taggedRecordId', person.id);
      }
      const uploaded = await upload<{ id: string }>(FILES_PATH, form, apiBaseUrl);
      if (!uploaded.ok) {
        setBusy(false);
        return setError(uploaded.message);
      }
      fileId = uploaded.data.id;
    }
    const answer = await post<{ id: string }>(
      NDA_PATH,
      {
        scopeType: scope,
        counterpartyCompanyId: scope === 'Person' ? null : counterparty?.id ?? null,
        scopeDealId: scope === 'Deal' ? deal?.id ?? null : null,
        scopePersonId: scope === 'Person' ? person?.id ?? null : null,
        fileId,
        effectiveDate,
        expiryDate: expiryDate || null,
        status: demanded ? 'Demanded' : 'Draft',
      },
      apiBaseUrl,
    );
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
    // Upload opens the questionnaire: the answers are what give the agreement its effects.
    navigate(`${NDA_REVIEW_ROUTE}/${answer.data.id}`);
  };

  return (
    <Modal open={open} title={t('nda.modal.title')} onCancel={onCancel} dataModal="nda" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <RadioGroup
        name="nda-scope"
        legend={t('nda.modal.scope')}
        value={scope}
        onChange={(value) => setScope(value as NdaScopeType)}
        options={[
          { value: 'Company', label: t('nda.modal.scopeCompany') },
          { value: 'Deal', label: t('nda.modal.scopeDeal') },
          { value: 'Person', label: t('nda.modal.scopePerson') },
        ]}
      />
      {scope !== 'Person' ? (
        <div className={styles.field} data-nda-field="counterparty">
          <Dropdown size={Size.Medium} label={t('nda.modal.counterparty')} entityLabel={t('nda.modal.counterpartyEntity')} search={companies} value={counterparty?.name ?? ''} onSelect={(row) => setCounterparty({ id: row.id, name: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord' }, (row) => setCounterparty({ id: row.id, name: row.line1 }))} />
        </div>
      ) : null}
      {scope === 'Deal' ? (
        <div className={styles.field} data-nda-field="deal">
          <Dropdown size={Size.Medium} label={t('nda.modal.deal')} entityLabel={t('nda.modal.deal')} search={deals} value={deal?.name ?? ''} onSelect={(row) => setDeal({ id: row.id, name: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Deal' }, (row) => setDeal({ id: row.id, name: row.line1 }))} />
        </div>
      ) : null}
      {scope === 'Person' ? (
        <div className={styles.field} data-nda-field="person">
          <Dropdown size={Size.Medium} label={t('nda.modal.person')} entityLabel={t('nda.modal.person')} search={people} value={person?.name ?? ''} onSelect={(row) => setPerson({ id: row.id, name: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'PersonRecord' }, (row) => setPerson({ id: row.id, name: row.line1 }))} />
        </div>
      ) : null}
      <DatePicker size={Size.Small} label={t('nda.modal.effective')} value={effectiveDate} onChange={setEffectiveDate} required name="nda-effective" />
      <DatePicker size={Size.Small} label={t('nda.modal.expiry')} value={expiryDate} onChange={setExpiryDate} name="nda-expiry" />
      <Checkbox label={t('nda.modal.demanded')} checked={demanded} onChange={setDemanded} size={Size.Small} />
      {demanded ? <p className={styles.hint}>{t('nda.modal.demandedHint')}</p> : null}
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="nda-file">{t('nda.modal.file')}</label>
        <input id="nda-file" type="file" accept="application/pdf" name="nda-file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-nda-field="file" />
      </div>
      {file ? (
        <Select size={Size.Medium} label={t('nda.modal.fileType')} value={fileTypeId} onChange={setFileTypeId} placeholder={t('edit.none')} options={fileTypes.map((row) => ({ value: row.id, label: row.name }))} dataAttribute={{ 'data-nda-field': 'fileType' }} />
      ) : null}
      {busy ? <p className={styles.hint}>{t('nda.modal.uploading')}</p> : null}
      {addNew.host}
    </Modal>
  );
}
